import { useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { SupplierSelect } from "@/features/suppliers";
import { CustomDatePicker, CustomInputField } from "@/features/ui";
import { OutletWarehouseSelect } from "@/features/warehouses";

export const GeneralInformationSection = ({ editMode = false }) => {
  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const buyer = watch("buyer");
  const sellerableType = watch("sellerableType");
  const goodsReceiptProductVariants = watch("goodsReceiptProductVariants");

  // Disable buyer and seller when there are existing products
  const disableBuyerSeller = goodsReceiptProductVariants.length > 0;

  useEffect(() => {
    if (editMode) return;

    if (buyer) {
      setValue("warehouse", null);
    }
  }, [buyer, editMode, setValue]);

  // Auto-set sellerableType based on buyer's role
  useEffect(() => {
    if (buyer) {
      const sellerableType =
        buyer.role === OutletRoleEnum.HUB ? "SUPPLIER" : "OUTLET";
      setValue("sellerableType", sellerableType);
    }
  }, [buyer, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (sellerableType) {
      setValue("seller", null);
    }
  }, [sellerableType, editMode, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) =>
              sellerableType === "OUTLET" ? (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyerSeller || !sellerableType}
                  allowedRoles={[OutletRoleEnum.HUB]}
                  TextFieldProps={{
                    label: "Pemasok (Hub)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <SupplierSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyerSeller || !sellerableType}
                  TextFieldProps={{
                    label: "Pemasok (Eksternal)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableBuyerSeller}
                TextFieldProps={{
                  label: "Outlet",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="warehouse"
            render={({ field, fieldState }) => (
              <OutletWarehouseSelect
                value={field.value}
                onChange={field.onChange}
                disabled={disableBuyerSeller}
                outletId={buyer?.id}
                TextFieldProps={{
                  label: "Gudang",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                  InputProps: {
                    placeholder: "Pilih Gudang",
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <CustomInputField
            required
            name="sellerReferenceNumber"
            label="No. Surat Jalan"
            register={register}
            helperText={errors.sellerReferenceNumber?.message}
            error={Boolean(errors.sellerReferenceNumber)}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
